import { z } from 'zod';
import { isoDateSchema, moneyMinorSchema } from '../api';
import { BED_CLASS } from '../enums';
export const listCursorSchema = z.string().uuid('That is not a cursor this list issued');
export const FOLLOW_UP_STATUS = ['pending', 'booked', 'done', 'cancelled'];
export const APPOINTMENT_FEE_ALERT = ['refund_due', 'fee_review'];
export const WARD_CHARGE_FREQUENCY = ['daily'];
export const WARD_CHARGE_PERFORMER = ['none', 'admitting_doctor'];
export const DEATH_SOURCE = ['ipd', 'er'];
export const CLOSED_ADMISSION_STATUS = ['discharged', 'lama', 'absconded', 'expired', 'referred'];
export const DUES_AGE_BUCKETS = ['0-30', '31-60', '61-90', '90+'];
export function duesAgeBucket(ageDays) {
    if (ageDays <= 30)
        return '0-30';
    if (ageDays <= 60)
        return '31-60';
    if (ageDays <= 90)
        return '61-90';
    return '90+';
}
export const REMINDER_HOURS_DEFAULT = [24, 2];
export const reminderHoursSchema = z
    .array(z.number().int().min(1).max(168))
    .min(1)
    .max(4)
    .refine((hours) => new Set(hours).size === hours.length, 'Each reminder time must be different');
export const followUpQuerySchema = z.object({
    status: z.enum(FOLLOW_UP_STATUS).optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    doctorId: z.string().uuid().optional(),
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: listCursorSchema.optional(),
});
export const updateFollowUpSchema = z.object({
    status: z.enum(['done', 'cancelled']),
    notes: z.string().trim().max(400).optional(),
});
export const addendumSchema = z.object({
    body: z.string().trim().min(5).max(4000),
});
export const wardChargeRuleSchema = z.object({
    name: z.string().trim().min(2).max(120),
    wardId: z.string().uuid().nullable().optional(),
    bedClass: z.enum(BED_CLASS).nullable().optional(),
    chargeItemId: z.string().uuid(),
    frequency: z.enum(WARD_CHARGE_FREQUENCY).default('daily'),
    performerKind: z.enum(WARD_CHARGE_PERFORMER).default('none'),
    isActive: z.boolean().default(true),
});
export const pauseWardChargeSchema = z
    .object({
    ruleId: z.string().uuid(),
    fromDate: isoDateSchema.optional(),
    toDate: isoDateSchema.nullable().optional(),
    reason: z.string().trim().min(3).max(300),
})
    .refine((value) => !value.fromDate || !value.toDate || value.toDate >= value.fromDate, {
    message: 'The pause cannot end before it starts',
    path: ['toDate'],
});
export const duesQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(1095).default(365),
    status: z.enum(CLOSED_ADMISSION_STATUS).optional(),
    q: z.string().trim().max(120).optional(),
    bucket: z.enum(DUES_AGE_BUCKETS).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
export const duesNoteSchema = z.object({
    note: z.string().trim().min(3).max(600),
    promisedDate: isoDateSchema.nullable().optional(),
    promisedAmountMinor: moneyMinorSchema.min(1).nullable().optional(),
});
export const deathCertificateSchema = z
    .object({
    admissionId: z.string().uuid().optional(),
    erCaseId: z.string().uuid().optional(),
})
    .refine((value) => Boolean(value.admissionId) !== Boolean(value.erCaseId), {
    message: 'Give either the admission or the ER case the death was recorded on',
    path: ['admissionId'],
});
export const mortalityQuerySchema = z
    .object({
    from: isoDateSchema,
    to: isoDateSchema,
    wardId: z.string().uuid().optional(),
})
    .refine((value) => value.to >= value.from, { message: 'The end date is before the start date', path: ['to'] });
export const pagedListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: listCursorSchema.optional(),
});
export const erIdentifySchema = z.object({
    patientId: z.string().uuid(),
    confirmed: z.boolean().default(false),
    basis: z.string().trim().max(300).optional(),
});
export const erMlcSchema = z.object({
    isMlc: z.boolean(),
    mlcDetails: z.string().trim().max(2000).nullable().optional(),
    reason: z.string().trim().min(3).max(300),
});
export const erBoardQuerySchema = pagedListQuerySchema.extend({
    branchId: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
export const revertDeceasedSchema = z.object({
    reason: z.string().trim().min(5).max(400),
});
export const visitorLocateQuerySchema = z.object({
    q: z.string().trim().min(3).max(80),
    branchId: z.string().uuid().optional(),
});
export const MIN_CLEANING_OVERRIDE_REASON = 5;
//# sourceMappingURL=operations.js.map